import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  IGeneralData,
  IOption,
  IProductDelivery,
  IproductReferenceDataInCountry,
  IShipmentData,
  ORDER_SHIPMENT_STATUS,
  SHIPMENT_STATUS,
  SHIPMENT_STATUS_ID,
  TEMPORARY_STORAGE_REASON_TYPE_ID
} from '../../../../../../../types/orders';
import { copyClipboard } from '../../../../../../../utils/copyClipboard';
import { autoUpdateSku, goToConfirmed } from '../../../../../../../services/traking.services';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../../utils/displayNudosStandardNotifications';
import { AxiosError } from 'axios';
import { getBringTotalAdditionalSettings } from '../../../../../../../utils/orders';
import { LOCATION_PLACE_TYPE } from '../../../../../../../types/services';
import { myCopyText } from '../../../../../../../utils/myCopyText';
import useOnlyDevelopers from 'state/setOnlyDevelopers';
import { IShipmentStatusTransitionConfig } from 'views/orders/Components/ShipmentStatusTransitionModal/useShipmentStatusTransition';
import { productHasActiveStockReservation } from 'utils/productStockReservation.utils';
import {
  displayConfigurationTransitionBlockedNotification,
  evaluateShipmentTransitToDestinationTransition,
  SHIPMENT_TRANSIT_TO_DESTINATION_BLOCK_MESSAGE
} from '../../../../../../../utils/productConfigurationSummary.utils';

const CONFIRMED_TO_PREPARATION_CONFIG: IShipmentStatusTransitionConfig = {
  sourceStatusId: SHIPMENT_STATUS_ID.CONFIRMED,
  targetStatusId: SHIPMENT_STATUS_ID.PREPARATION,
  modalTitle: 'De Confirmado a Preparación',
  primaryButtonText: 'Mover a Preparación',
  headerTitle: 'Elige las herramientas que pasan a Preparación',
  successMessage: <>Herramientas movidas a Preparación con éxito</>
};

const PREPARATION_TO_TRANSIT_TO_WAREHOUSE_CONFIG: IShipmentStatusTransitionConfig = {
  sourceStatusId: SHIPMENT_STATUS_ID.PREPARATION,
  targetStatusId: SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE,
  modalTitle: 'De Preparación a Tránsito a bodega',
  primaryButtonText: 'Mover a Tránsito a bodega',
  headerTitle: 'Elige las herramientas que pasan a Tránsito a bodega',
  successMessage: <>Herramientas movidas a Tránsito a bodega correctamente</>,
  requiresTransitTrackingInfo: true
};

const TRANSIT_TO_WAREHOUSE_EDIT_WTS_CONFIG: IShipmentStatusTransitionConfig = {
  targetStatusId: SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE,
  modalTitle: 'Editar tránsito a bodega',
  primaryButtonText: 'Guardar cambios',
  headerTitle: 'Actualiza las herramientas que pasan a Tránsito a bodega',
  successMessage: <>Información de tránsito a bodega actualizada</>,
  requiresTransitTrackingInfo: true,
  skipShipmentStatusMassUpdate: true
};

const TRANSIT_TO_IN_WAREHOUSE_CONFIG: IShipmentStatusTransitionConfig = {
  sourceStatusId: SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE,
  targetStatusId: SHIPMENT_STATUS_ID.WAREHOUSE_STOP,
  modalTitle: 'De Tránsito a bodega a En bodega',
  primaryButtonText: 'Mover a En bodega',
  headerTitle: 'Elige las herramientas que pasan a En Bodega',
  customAlertMessage:
    'Al confirmar, la herramienta aparecerá en WMS y quedará Por escanear en Cross Docking.',
  successMessage: <>La herramienta está en bodega. Ahora ve a WMS y escanéala en Cross Docking.</>,
  requiresSerials: true
};

const PREPARATION_TO_TRANSIT_TO_DESTINATION_CONFIG: IShipmentStatusTransitionConfig = {
  sourceStatusId: SHIPMENT_STATUS_ID.PREPARATION,
  targetStatusId: SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION,
  modalTitle: 'De Preparación a Tránsito al destino',
  primaryButtonText: 'Mover a Tránsito al destino',
  headerTitle: 'Elige las herramientas que pasan a Tránsito al destino',
  successMessage: <>Herramientas movidas a Tránsito al destino correctamente</>,
  requiresTransitTrackingInfo: true
};

const WAREHOUSE_TO_TRANSIT_TO_DESTINATION_CONFIG: IShipmentStatusTransitionConfig = {
  sourceStatusId: SHIPMENT_STATUS_ID.WAREHOUSE_STOP,
  targetStatusId: SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION,
  modalTitle: 'De En bodega a Tránsito al destino',
  primaryButtonText: 'Mover a Tránsito al destino',
  headerTitle: 'Elige las herramientas que pasan a Tránsito al destino',
  successMessage: <>Herramientas movidas a Tránsito al destino correctamente</>,
  requiresTransitTrackingInfo: true
};

export const TRANSIT_TO_DESTINATION_TO_DELIVERED_CONFIG: IShipmentStatusTransitionConfig = {
  sourceStatusId: SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION,
  targetStatusId: SHIPMENT_STATUS_ID.DELIVERED,
  modalTitle: 'De Tránsito al destino a Entregado',
  primaryButtonText: 'Mover a Entregado',
  headerTitle: 'Elige las herramientas que pasan a Entregado',
  successMessage: <>Envío cambiado a Entregado correctamente</>,
  requiresDeliveryDate: true,
  requiresSerials: true,
  secondStepVerificationText: ({ destinationName }) =>
    `Cambiar a Entregado el envío a ${destinationName}`
};

export interface IUseShipmentCardTrackingParams {
  listOfGroupedShipments: IShipmentData[];
  completeDataForEachGroupOfShipments: IShipmentData;
  productsInShippingGroup: IProductDelivery[];
  isEnabled: boolean;
  index: number;
  orderId: number;
  generalData: IGeneralData;
  updateWithoutLoaders?: () => Promise<void>;
  updateWithLoaders?: () => Promise<void>;
  references: IproductReferenceDataInCountry[];
  isLastCardInRenderedList?: boolean;
}

const OPTIONS_LIST: IOption[] = [
  { name: SHIPMENT_STATUS.PENDING, ShipmentStatusId: SHIPMENT_STATUS_ID.PENDING },
  { name: SHIPMENT_STATUS.CONFIRMED, ShipmentStatusId: SHIPMENT_STATUS_ID.CONFIRMED },
  { name: ORDER_SHIPMENT_STATUS.PREPARATION, ShipmentStatusId: SHIPMENT_STATUS_ID.PREPARATION },
  { name: ORDER_SHIPMENT_STATUS.TRANSIT_TO_WAREHOUSE, ShipmentStatusId: SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE },
  { name: ORDER_SHIPMENT_STATUS.IN_WAREHOUSE, ShipmentStatusId: SHIPMENT_STATUS_ID.WAREHOUSE_STOP },
  { name: ORDER_SHIPMENT_STATUS.TRANSIT_TO_DESTINATION, ShipmentStatusId: SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION },
  { name: SHIPMENT_STATUS.DELIVERED, ShipmentStatusId: SHIPMENT_STATUS_ID.DELIVERED },
  { name: SHIPMENT_STATUS.CANCELLED, ShipmentStatusId: SHIPMENT_STATUS_ID.CANCELLED }
];

/**
 * Hook con la lógica de estado, transiciones de estado y acciones del shipment card (tracking).
 * Centraliza estados, opciones del dropdown de estado y handlers de cambio de estado, SKU y modales.
 */
export const useShipmentCardTracking = ({
  listOfGroupedShipments,
  completeDataForEachGroupOfShipments,
  productsInShippingGroup,
  isEnabled,
  index,
  orderId,
  generalData,
  updateWithoutLoaders,
  references,
  isLastCardInRenderedList
}: IUseShipmentCardTrackingParams) => {
  const [idLoad, setIdLoad] = useState<number>();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [selectOption, setSelectOption] = useState<IOption>();
  const [showProcessModal, setShowProcessModal] = useState<boolean>(false);
  const [showProcessModalConfirmation, setShowProcessModalConfirmation] = useState<boolean>(false);
  const [isEditedProcesModal, setIsEditedProcessModal] = useState<boolean>(false);
  const [editSkuModal, setEditSkuModal] = useState<IProductDelivery>();
  const [loadingSku, setLoadingSku] = useState<boolean>(false);
  const [notFoundSku, setNotFoundSku] = useState<boolean>(false);
  const [showPendingConfigurationModal, setShowPendingConfigurationModal] = useState<boolean>(false);
  const [showSerialsModal, setShowSerialsModal] = useState<IProductDelivery>();
  const [showShipmentStatusTransitionModal, setShowShipmentStatusTransitionModal] = useState<boolean>(false);
  const [showTransitToWarehouseReasonModal, setShowTransitToWarehouseReasonModal] = useState<boolean>(false);
  const transitToWarehouseAfterReasonIsEditOnlyRef = useRef(false);
  const [transitToWarehouseModalInitialReasons, setTransitToWarehouseModalInitialReasons] = useState<
    TEMPORARY_STORAGE_REASON_TYPE_ID[] | undefined
  >(undefined);
  const [pendingWarehouseStopReasonsForTransition, setPendingWarehouseStopReasonsForTransition] = useState<
    TEMPORARY_STORAGE_REASON_TYPE_ID[] | null
  >(null);
  const [lastSerialsByProductId, setLastSerialsByProductId] = useState<Record<number, string>>({});
  const [transitionConfigForModal, setTransitionConfigForModal] =
    useState<IShipmentStatusTransitionConfig | null>(null);
  const [pendingTransitionConfigForConfirm, setPendingTransitionConfigForConfirm] =
    useState<IShipmentStatusTransitionConfig | null>(null);
  /** isOnlyDevsLogistics aqui */
  const { isOnlyDevsLogistics } = useOnlyDevelopers();

  const productInformationForThisShipment = productsInShippingGroup[0];
  const warehouseDestination = completeDataForEachGroupOfShipments?.place === LOCATION_PLACE_TYPE.NUDOS;
  const lastItemInTheList =
    isLastCardInRenderedList ?? index + 1 === listOfGroupedShipments?.length;
  const countryTimeZone = completeDataForEachGroupOfShipments?.country?.timeZone || undefined;
  const status = completeDataForEachGroupOfShipments?.status;
  const isBuyAndHold = completeDataForEachGroupOfShipments?.place === LOCATION_PLACE_TYPE.NUDOS;

  const optionsListForDropdown = isBuyAndHold
    ? OPTIONS_LIST.filter(
        opt =>
          opt.ShipmentStatusId !== SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE &&
          opt.ShipmentStatusId !== SHIPMENT_STATUS_ID.WAREHOUSE_STOP
      )
    : OPTIONS_LIST;

  const totalAdditionalSettings = getBringTotalAdditionalSettings(
    productInformationForThisShipment.sku,
    completeDataForEachGroupOfShipments?.shipmentGroupId,
    references || [],
    productsInShippingGroup?.map(p => p.id)
  );

  const hasUnconfirmedActions = totalAdditionalSettings.withUnconfirmedActions > 0;
  const existPendingConfigurations =
    !!totalAdditionalSettings?.toBeCompleted && totalAdditionalSettings.toBeCompleted > 0;
  const hasStockSerials = references
    .filter(reference => reference.sku === productInformationForThisShipment?.sku)
    .map(reference => reference.items)
    .flat()
    .some(item => item?.stockReservation?.stockProduct?.serial);

  const existSerials =
    hasStockSerials || completeDataForEachGroupOfShipments?.products?.some(product => !!product?.serial);

  const singleProduct = {
    name: productInformationForThisShipment?.name,
    pills: productInformationForThisShipment?.pills,
    units: productsInShippingGroup?.length
  };

  const statusesAllowingSkuEditing = [
    SHIPMENT_STATUS.PENDING,
    SHIPMENT_STATUS.CONFIRMED,
    ORDER_SHIPMENT_STATUS.PREPARATION,
    ORDER_SHIPMENT_STATUS.TRANSIT_TO_WAREHOUSE,
    ORDER_SHIPMENT_STATUS.IN_WAREHOUSE,
    ORDER_SHIPMENT_STATUS.TRANSIT_TO_DESTINATION
  ];

  const allowSkuEditing = statusesAllowingSkuEditing.some(
    allowed => allowed.toLowerCase() === (status ?? '').toLowerCase()
  );

  const isStockReservation = references
    .map(reference => reference.items)
    .flat()
    .some(item => item?.stockReservation !== null);

  const shipmentProducts = productsInShippingGroup ?? [];
  /** isOnlyDevsLogistics aqui */
  const allShipmentProductsActiveStockReservation =
    ( isOnlyDevsLogistics && shipmentProducts.length > 0 ) &&
    shipmentProducts.every(product => productHasActiveStockReservation(product, references ?? []));

  const hasPendingConfigMessage = (hasUnconfirmed: boolean) => {
    if (hasUnconfirmed) {
      return 'El cliente todavía no ha confirmado la(s) configuración(es) de todas las herramientas, ¿seguro que deseas cambiar el estado?';
    }
    return 'Estás a punto de cambiar el estado sin completar las configuraciones adicionales, ¿seguro que deseas continuar?';
  };

  /**
   * Valida si el envío puede pasar a Tránsito al destino según el summary del backend.
   * @param targetShipmentId - ID del envío a evaluar
   */
  const validateTransitToDestinationTransition = async (targetShipmentId?: number): Promise<boolean> => {
    if (!targetShipmentId) return false;
    if (!isOnlyDevsLogistics) return true;

    try {
      const { isBlocked } = await evaluateShipmentTransitToDestinationTransition(targetShipmentId);

      if (!isBlocked) return true;

      displayConfigurationTransitionBlockedNotification({
        displayErrorNotification,
        mainMessage: SHIPMENT_TRANSIT_TO_DESTINATION_BLOCK_MESSAGE
      });

      return false;
    } catch {
      displayErrorNotification();
      return false;
    }
  };

  const getAllowedNextStatusIds = (currentStatusId: number): number[] => {
    const defaultMap: Record<number, number[]> = {
      [SHIPMENT_STATUS_ID.PENDING]: [SHIPMENT_STATUS_ID.CONFIRMED],
      [SHIPMENT_STATUS_ID.CONFIRMED]: [SHIPMENT_STATUS_ID.PREPARATION],
      [SHIPMENT_STATUS_ID.PREPARATION]: [
        SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE,
        SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION
      ],
      [SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE]: [SHIPMENT_STATUS_ID.WAREHOUSE_STOP],
      [SHIPMENT_STATUS_ID.WAREHOUSE_STOP]: [SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION],
      [SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION]: [SHIPMENT_STATUS_ID.DELIVERED]
    };

    if (isBuyAndHold && currentStatusId === SHIPMENT_STATUS_ID.PREPARATION) {
      return [SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION];
    }

    if (
      currentStatusId === SHIPMENT_STATUS_ID.PREPARATION &&
      allShipmentProductsActiveStockReservation
    ) {
      return [SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION];
    }

    return defaultMap[currentStatusId] ?? [];
  };

  const diabledSelect = (selectId: number, statusId: number) => {
    const numStatusId = Number(statusId) || SHIPMENT_STATUS_ID.PENDING;
    if (selectId === numStatusId) return { show: false, enabled: false };
    const allowedNext = getAllowedNextStatusIds(numStatusId);
    const isAllowed = allowedNext.includes(selectId);

    return { show: allowedNext.includes(selectId), enabled: isAllowed };
  };

  const changeStatusLogic = async (option: IOption) => {
    if (!option) return;

    const selectedStatus = option.ShipmentStatusId;
    const currentStatus = completeDataForEachGroupOfShipments?.statusId;
    const shipmentId = completeDataForEachGroupOfShipments?.shipmentId;

    setIdLoad(shipmentId);

    try {
      const directStatusIds = [
        SHIPMENT_STATUS_ID.CONFIRMED,
        SHIPMENT_STATUS_ID.PREPARATION,
        SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE,
        SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION
      ];

      const isConfirmedToPreparation =
        currentStatus === SHIPMENT_STATUS_ID.CONFIRMED && selectedStatus === SHIPMENT_STATUS_ID.PREPARATION;

      if (isConfirmedToPreparation) {
        if (existPendingConfigurations || hasUnconfirmedActions) {
          setPendingTransitionConfigForConfirm(CONFIRMED_TO_PREPARATION_CONFIG);
          setShowPendingConfigurationModal(true);
          return;
        }

        setTransitionConfigForModal(CONFIRMED_TO_PREPARATION_CONFIG);
        setShowShipmentStatusTransitionModal(true);
        return;
      }

      const isPreparationToTransitToWarehouse =
        currentStatus === SHIPMENT_STATUS_ID.PREPARATION &&
        selectedStatus === SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE;

      if (isPreparationToTransitToWarehouse) {
        transitToWarehouseAfterReasonIsEditOnlyRef.current = false;
        setTransitToWarehouseModalInitialReasons(undefined);
        setShowTransitToWarehouseReasonModal(true);
        return;
      }

      const isTransitToWarehouseToInWarehouse =
        selectedStatus === SHIPMENT_STATUS_ID.WAREHOUSE_STOP &&
        currentStatus === SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE;

      if (isTransitToWarehouseToInWarehouse) {
        setTransitionConfigForModal(TRANSIT_TO_IN_WAREHOUSE_CONFIG);
        setShowShipmentStatusTransitionModal(true);
        return;
      }

      const isPreparationToTransitToDestination =
        currentStatus === SHIPMENT_STATUS_ID.PREPARATION &&
        selectedStatus === SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION;

      if (isPreparationToTransitToDestination) {
        if (!(await validateTransitToDestinationTransition(shipmentId))) return;
        setTransitionConfigForModal(PREPARATION_TO_TRANSIT_TO_DESTINATION_CONFIG);
        setShowShipmentStatusTransitionModal(true);
        return;
      }

      const isWarehouseToTransitToDestination =
        currentStatus === SHIPMENT_STATUS_ID.WAREHOUSE_STOP &&
        selectedStatus === SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION;

      if (isWarehouseToTransitToDestination) {
        if (!(await validateTransitToDestinationTransition(shipmentId))) return;
        setTransitionConfigForModal(WAREHOUSE_TO_TRANSIT_TO_DESTINATION_CONFIG);
        setShowShipmentStatusTransitionModal(true);
        return;
      }

      const isTransitToDestinationToDelivered =
        currentStatus === SHIPMENT_STATUS_ID.TRANSIT_TO_DESTINATION &&
        selectedStatus === SHIPMENT_STATUS_ID.DELIVERED;

      if (isTransitToDestinationToDelivered) {
        setTransitionConfigForModal(TRANSIT_TO_DESTINATION_TO_DELIVERED_CONFIG);
        setShowShipmentStatusTransitionModal(true);
        return;
      }

      if (directStatusIds.includes(selectedStatus)) {
        await goToConfirmed(shipmentId, selectedStatus);
        await updateWithoutLoaders?.();
        displaySuccessNotification({ customJSXMessage: <>Estado actualizado correctamente</> });
        return;
      }
    } catch {
      displayErrorNotification();
    } finally {
      setIdLoad(undefined);
    }
  };

  const generateOptions = (shipmentData: IShipmentData) => {
    const currentStatusId = Number(shipmentData?.statusId) || SHIPMENT_STATUS_ID.PENDING;
    return optionsListForDropdown.map((item, optionIndex) => {
      const { show: renderDisabledSelectShow, enabled: renderDisabledSelectEnabled } = diabledSelect(
        item?.ShipmentStatusId,
        currentStatusId
      );
      return (
        <Fragment key={`option-${item?.ShipmentStatusId}-${optionIndex}`}>
          {renderDisabledSelectShow && (
            <div
              className="optionStyle"
              onClick={() => {
                if (renderDisabledSelectEnabled) changeStatusLogic(item);
              }}
            >
              <div className={`optionText ${!renderDisabledSelectEnabled ? 'disabledSelect' : ''} `}>{item?.name} </div>
            </div>
          )}
        </Fragment>
      );
    });
  };

  const assingStatus = () => {
    const statusId = completeDataForEachGroupOfShipments?.statusId;

    if (statusId === SHIPMENT_STATUS_ID.DELIVERY_PROCESS) {
      setSelectOption({
        name: SHIPMENT_STATUS.DELIVERY_PROCESS,
        ShipmentStatusId: SHIPMENT_STATUS_ID.DELIVERY_PROCESS
      });
      return;
    }

    const findState = OPTIONS_LIST.find(element => statusId === element.ShipmentStatusId);
    if (findState) setSelectOption(findState);
  };

  const handleChangeFoundSku = (error: boolean) => setNotFoundSku(error);

  const updateSKU = async (newSKU: string) => {
    setLoadingSku(true);

    const IdsList = productsInShippingGroup.map(item => item?.id);
    const bodySkus = {
      productsIds: IdsList,
      newSku: newSKU?.replaceAll(' ', '')
    };

    try {
      await autoUpdateSku(bodySkus);
      await updateWithoutLoaders?.();
      setEditSkuModal(undefined);

      displaySuccessNotification({
        customJSXMessage: <>Se realizo la solicitud de manera correcta, en un rato debes encontrar el SKU actualizado</>
      });
    } catch (err) {
      const axiosError = err as AxiosError;

      if (axiosError?.response?.data?.message === 'sku not found') {
        handleChangeFoundSku(true);
        displayErrorNotification({
          customJSXMessage: <>Este SKU no existe en el catalogo</>
        });
      }
    } finally {
      setLoadingSku(false);
    }
  };

  const handleCopyProductInfo = () => {
    copyClipboard({
      text: myCopyText({
        productInfo: {
          ...productInformationForThisShipment,
          unit: references.find(r => r.productName === productInformationForThisShipment.name)?.quantity?.toString()
        }
      })
    });
  };

  const getSerialActionText = (): string => {
    if (hasStockSerials || existSerials) return 'Ver/editar seriales';
    return 'Agregar seriales';
  };

  const handleTransitionConfirmed = (shipments: IShipmentData[]) => {
    setLastSerialsByProductId(prev => {
      const next = { ...prev };
      shipments.forEach(shipment =>
        shipment.products?.forEach(p => {
          if (p?.id != null && p?.serial?.trim()) next[p.id] = p.serial!.trim();
        })
      );
      return next;
    });
  };

  const handleConfirmPendingConfiguration = useCallback(() => {
    if (pendingTransitionConfigForConfirm) {
      setTransitionConfigForModal(pendingTransitionConfigForConfirm);
      setShowShipmentStatusTransitionModal(true);
    }
    setShowPendingConfigurationModal(false);
    setPendingTransitionConfigForConfirm(null);
  }, [pendingTransitionConfigForConfirm]);

  const setShowPendingConfigurationModalWithCleanup = useCallback((value: React.SetStateAction<boolean>) => {
    if (typeof value === 'function') {
      setShowPendingConfigurationModal(prev => {
        const next = (value as (prev: boolean) => boolean)(prev);
        if (next === false) setPendingTransitionConfigForConfirm(null);
        return next;
      });
    } else {
      if (value === false) setPendingTransitionConfigForConfirm(null);
      setShowPendingConfigurationModal(value);
    }
  }, []);

  const isShipmentStatusSelectDeactivated =
    !isEnabled ||
    selectOption?.ShipmentStatusId === SHIPMENT_STATUS_ID.DELIVERED ||
    selectOption?.ShipmentStatusId === SHIPMENT_STATUS_ID.DELIVERY_PROCESS;

  const productsWithResolvedSerials = productsInShippingGroup.map(p => {
    const serialFromApi = p.serial?.trim();
    const serialFromCache = p.id != null ? lastSerialsByProductId[p.id] : undefined;
    return {
      ...p,
      serial: serialFromApi || serialFromCache || (p.serial ?? '')
    };
  });

  const listOfShipmentsAvailableToAddForTransition =
    transitionConfigForModal?.sourceStatusId != null
      ? listOfGroupedShipments?.filter(
          s =>
            s.statusId === transitionConfigForModal.sourceStatusId &&
            s.shipmentId !== completeDataForEachGroupOfShipments?.shipmentId
        ) ?? []
      : undefined;

  const shouldShowPaymentFloatMessage =
    showMessage &&
    completeDataForEachGroupOfShipments?.statusId !== SHIPMENT_STATUS_ID.DELIVERED &&
    idLoad !== completeDataForEachGroupOfShipments?.shipmentId;

  const handleTransitToWarehouseReasonConfirm = (reasons: TEMPORARY_STORAGE_REASON_TYPE_ID[]) => {
    const useEditOnlyConfig = transitToWarehouseAfterReasonIsEditOnlyRef.current;
    transitToWarehouseAfterReasonIsEditOnlyRef.current = false;
    setPendingWarehouseStopReasonsForTransition(reasons);
    setShowTransitToWarehouseReasonModal(false);
    setTransitionConfigForModal(
      useEditOnlyConfig ? TRANSIT_TO_WAREHOUSE_EDIT_WTS_CONFIG : PREPARATION_TO_TRANSIT_TO_WAREHOUSE_CONFIG
    );
    setShowShipmentStatusTransitionModal(true);
  };

  /**
   * Abre el modal de motivos y marca el flujo como «solo actualizar WTS» (sin `shipment-status-masive`).
   * Tras confirmar motivos se usa `TRANSIT_TO_WAREHOUSE_EDIT_WTS_CONFIG`.
   */
  const handleOpenEditTransitToWarehouseFromTracking = useCallback(() => {
    transitToWarehouseAfterReasonIsEditOnlyRef.current = true;
    const wts = completeDataForEachGroupOfShipments?.warehouseTemporaryStorage;
    const ids =
      wts?.warehouseTemporaryStorageReasons?.map(
        r => r.temporaryStorageReasonId as TEMPORARY_STORAGE_REASON_TYPE_ID
      ) ?? [];
    setTransitToWarehouseModalInitialReasons(ids.length > 0 ? ids : undefined);
    setShowTransitToWarehouseReasonModal(true);
  }, [completeDataForEachGroupOfShipments]);

  const handleShipmentStatusTransitionModalClose = (action: boolean | ((prev: boolean) => boolean)) => {
    const next = typeof action === 'function' ? action(showShipmentStatusTransitionModal) : action;
    setShowShipmentStatusTransitionModal(next);

    if (next === false) {
      setTransitionConfigForModal(null);
      setPendingWarehouseStopReasonsForTransition(null);
    }
  };

  const handleEditSkuClick = () => {
    if (!productInformationForThisShipment?.isRequestedChange) {
      setEditSkuModal(productInformationForThisShipment);
    }
  };

  useEffect(() => {
    if (completeDataForEachGroupOfShipments?.status) assingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeDataForEachGroupOfShipments]);

  useEffect(() => {
    if (!showTransitToWarehouseReasonModal) {
      setTransitToWarehouseModalInitialReasons(undefined);
      transitToWarehouseAfterReasonIsEditOnlyRef.current = false;
    }
  }, [showTransitToWarehouseReasonModal]);

  return {
    idLoad,
    showMessage,
    setShowMessage,
    selectOption,
    showProcessModal,
    setShowProcessModal,
    showProcessModalConfirmation,
    setShowProcessModalConfirmation,
    isEditedProcesModal,
    setIsEditedProcessModal,
    editSkuModal,
    setEditSkuModal,
    loadingSku,
    notFoundSku,
    showPendingConfigurationModal,
    setShowPendingConfigurationModal: setShowPendingConfigurationModalWithCleanup,
    handleConfirmPendingConfiguration,
    showSerialsModal,
    setShowSerialsModal,
    showShipmentStatusTransitionModal,
    setShowShipmentStatusTransitionModal,
    transitionConfigForModal,
    setTransitionConfigForModal,
    productInformationForThisShipment,
    totalAdditionalSettings,
    warehouseDestination,
    lastItemInTheList,
    hasUnconfirmedActions,
    hasStockSerials,
    existSerials,
    countryTimeZone,
    singleProduct,
    allowSkuEditing,
    isStockReservation,
    hasPendingConfigMessage,
    generateOptions,
    handleChangeFoundSku,
    updateSKU,
    handleCopyProductInfo,
    getSerialActionText,
    lastSerialsByProductId,
    handleTransitionConfirmed,
    isShipmentStatusSelectDeactivated,
    productsWithResolvedSerials,
    listOfShipmentsAvailableToAddForTransition,
    shouldShowPaymentFloatMessage,
    handleShipmentStatusTransitionModalClose,
    handleEditSkuClick,
    showTransitToWarehouseReasonModal,
    setShowTransitToWarehouseReasonModal,
    handleTransitToWarehouseReasonConfirm,
    pendingWarehouseStopReasonsForTransition,
    transitToWarehouseModalInitialReasons,
    handleOpenEditTransitToWarehouseFromTracking
  };
};
